/**
 * Schedule — weekly hours strip, Today tile, then the week below (brief §3 + owner request).
 * Each job row has two tap areas: the camera opens "Add site note" for that job,
 * the job itself opens the Job Detail screen for that day.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { WeeklyHoursStrip } from '../../components/portal/WeeklyHoursStrip';
import { ChevronLeft, ChevronRight, MapPin, Loader2, Camera, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, isSameDay, isBefore, startOfDay } from 'date-fns';
import { jobCode, isConstruction } from '../../utils/jobCode';

const iso = (d) => format(d, 'yyyy-MM-dd');
const isCon = isConstruction;

function JobCard({ job, emphasis = false }) {
  const navigate = useNavigate();
  const { features } = useAuth();
  const con = isCon(job);
  const code = jobCode(job, features);
  const name = (job.project_ref || '').split('-').slice(2).join('-') || job.client_name || '';
  const openJob = () => navigate(`/portal/job/${job.project_id}?date=${job.date}`);
  const addNote = (e) => { e.stopPropagation(); navigate(`/portal/site-notes/new?project=${job.project_id}`); };
  return (
    <div className={`rounded-xl flex items-stretch overflow-hidden ${emphasis ? 'bg-[#1E2530] border border-[#F5A623]/30' : 'bg-[#1E2530]'}`} data-testid={`schedule-job-${job.id}`}>
      <button type="button" onClick={addNote} className={`w-16 shrink-0 flex flex-col items-center justify-center gap-1 border-r border-white/10 active:scale-95 ${emphasis ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-white/5 text-[#F5A623]'}`} aria-label="Add site note" data-testid={`schedule-job-note-${job.id}`}>
        <Camera className="w-6 h-6" />
        <span className="text-[10px] font-semibold">Note</span>
      </button>
      <button type="button" onClick={openJob} className="flex-1 min-w-0 text-left p-3 active:bg-white/5" data-testid={`schedule-job-open-${job.id}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`font-bold ${con ? 'text-blue-400' : 'text-[#F5A623]'}`} data-testid={`schedule-job-code-${job.id}`}>{code}</span>
          <span className="text-white font-semibold truncate">{name}</span>
          <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full border ${con ? 'bg-blue-500/15 text-blue-300 border-blue-500/30' : 'bg-[#F5A623]/15 text-[#F5A623] border-[#F5A623]/30'}`}>{con ? 'Construction' : 'Developments'}</span>
        </div>
        <div className="flex items-start gap-2 mt-2 text-gray-400 text-sm">
          <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex flex-col min-w-0"><span className="truncate">{job.address || 'Address not set'}</span>{job.postcode && <span className="font-medium text-gray-300">{job.postcode}</span>}</div>
          <ChevronRight className="w-5 h-5 text-gray-500 ml-auto shrink-0 self-center" />
        </div>
      </button>
    </div>
  );
}

export function PortalSchedule() {
  const { api, features } = useAuth();
  const navigate = useNavigate();
  const today = startOfDay(new Date());
  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState([]);
  const [todayJobs, setTodayJobs] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(today, { weekStartsOn: 1 }));

  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/portal/my-schedule?start_date=${iso(weekStart)}&end_date=${iso(addDays(weekStart, 6))}`);
      setSchedule(r.data);
    } catch { toast.error('Failed to load schedule'); }
    finally { setLoading(false); }
  }, [api, weekStart]);
  useEffect(() => { fetchWeek(); }, [fetchWeek]);
  useEffect(() => {
    api.get(`/portal/my-schedule?start_date=${iso(today)}&end_date=${iso(today)}`).then(r => setTodayJobs(r.data)).catch(() => setTodayJobs([]));
  }, [api]); // eslint-disable-line react-hooks/exhaustive-deps

  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const onThisWeek = isSameDay(weekStart, startOfWeek(today, { weekStartsOn: 1 }));

  return (
    <div className="p-4 space-y-4" data-testid="portal-schedule">
      <WeeklyHoursStrip weekStart={weekStart} schedule={schedule} />

      <section className="rounded-2xl bg-[#F5A623] text-[#1E2530] p-4 shadow-lg shadow-[#F5A623]/10" data-testid="today-tile">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest opacity-70 flex items-center gap-1"><Sun className="w-3.5 h-3.5" /> Today</div>
            <div className="text-2xl font-black leading-tight" data-testid="today-date">{format(today, 'EEEE d MMMM')}</div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-black tabular-nums" data-testid="today-count">{todayJobs ? todayJobs.length : '–'}</div>
            <div className="text-[11px] font-semibold opacity-70">{todayJobs?.length === 1 ? 'job' : 'jobs'}</div>
          </div>
        </div>
        <div className="mt-3 space-y-2">
          {todayJobs === null && <div className="py-3 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto opacity-60" /></div>}
          {todayJobs?.length === 0 && (
            <div className="rounded-xl bg-[#1E2530]/10 border border-[#1E2530]/15 p-3 text-sm font-medium" data-testid="today-empty">Nothing scheduled for you today.</div>
          )}
          {todayJobs?.map(job => <JobCard key={job.id} job={job} emphasis />)}
        </div>
      </section>

      <div className="flex items-center justify-between rounded-xl bg-[#2A3240] border border-white/10 px-2 py-1" data-testid="week-nav">
        <Button variant="ghost" size="sm" onClick={() => setWeekStart(d => addDays(d, -7))} className="text-gray-400 hover:text-white min-h-[44px] min-w-[44px] active:scale-95" data-testid="week-prev"><ChevronLeft className="w-6 h-6" /></Button>
        <button type="button" onClick={() => setWeekStart(startOfWeek(today, { weekStartsOn: 1 }))} className="text-center active:scale-95" data-testid="week-label">
          <div className="text-white font-semibold text-sm">{format(weekStart, 'd MMM')} – {format(addDays(weekStart, 6), 'd MMM yyyy')}</div>
          <div className="text-[11px] text-[#F5A623]">{onThisWeek ? 'This week' : 'Tap for this week'}</div>
        </button>
        <Button variant="ghost" size="sm" onClick={() => setWeekStart(d => addDays(d, 7))} className="text-gray-400 hover:text-white min-h-[44px] min-w-[44px] active:scale-95" data-testid="week-next"><ChevronRight className="w-6 h-6" /></Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" /></div>
      ) : (
        <div className="space-y-2" data-testid="week-days">
          {weekDays.map(day => {
            const jobs = schedule.filter(s => s.date === iso(day));
            const isToday = isSameDay(day, today);
            const past = isBefore(day, today);
            return (
              <div key={iso(day)} className={`rounded-xl border p-3 flex gap-3 ${isToday ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-white/10 bg-[#2A3240]'} ${past && !isToday ? 'opacity-60' : ''}`} data-testid={`day-${iso(day)}`} data-today={isToday ? 'true' : undefined}>
                <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center shrink-0 ${isToday ? 'bg-[#F5A623] text-[#1E2530]' : 'bg-[#1E2530] text-gray-300'}`}>
                  <span className="text-[10px] font-bold uppercase">{format(day, 'EEE')}</span>
                  <span className="text-lg font-black leading-none">{format(day, 'd')}</span>
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  {isToday && <div className="text-[10px] font-bold uppercase tracking-wider text-[#F5A623]">Today</div>}
                  {jobs.length === 0 ? <div className="h-12 flex items-center text-gray-500 text-sm">No jobs</div> : jobs.map(job => (
                    <div key={job.id} className="flex items-stretch rounded-lg bg-[#1E2530] overflow-hidden" data-testid={`week-job-${job.id}`}>
                      <button type="button" onClick={() => navigate(`/portal/site-notes/new?project=${job.project_id}`)} className="w-11 shrink-0 flex items-center justify-center text-[#F5A623] bg-white/5 border-r border-white/10 active:scale-95" aria-label="Add site note" data-testid={`week-job-note-${job.id}`}>
                        <Camera className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => navigate(`/portal/job/${job.project_id}?date=${job.date}`)} className="flex-1 min-w-0 text-left px-3 py-2 active:bg-white/5" data-testid={`week-job-open-${job.id}`}>
                        <div className="flex items-center gap-2"><span className={`font-bold text-sm ${isCon(job) ? 'text-blue-400' : 'text-[#F5A623]'}`}>{jobCode(job, features)}</span><span className="text-white text-sm truncate">{(job.project_ref || '').split('-').slice(2).join('-') || job.client_name}</span><ChevronRight className="w-4 h-4 text-gray-500 ml-auto shrink-0" /></div>
                        <div className="text-xs text-gray-400 truncate">{[job.address, job.postcode].filter(Boolean).join(', ') || 'Address not set'}</div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
